<template>
    <div class="stepper-box">
        <div class="top">
            <div 
                class="divider-line" 
                :style="{width: `${(100/(steps.length) * (steps.length - 1)) - 10}%`}"
            />

            <div class="steps-wrapper">
                <template v-if="topButtons">
                    <div 
                        v-if="currentStep.index > 0" 
                        class="stepper-button-top previous" 
                        @click="backStep()"
                    >
                        <i class="material-icons">keyboard_arrow_left</i>
                    </div>
                </template>

                <template v-for="(step, index) in steps">
                    <div 
                        :class="['step', isStepActive(index, step)]" 
                        :key="index" 
                        :style="{width: `${100 / steps.length}%`}"
                    >
                        <div class="circle-icon">
                            <i class="material-icons md-18">
                                {{ (step.completed) ? 'done' : step.icon }}
                            </i>
                        </div>

                        <div class="step-title">
                            <h4>{{step.title}}</h4>
                            <h5 class="step-subtitle">{{step.subtitle}}</h5>
                        </div>

                    </div>
                </template>

                <div 
                    v-if="topButtons" 
                    :class="['stepper-button-top next', !canContinue ? 'deactivated' : '']" 
                    @click="nextStep()"
                >
                    <i class="material-icons">keyboard_arrow_right</i>
                </div>

            </div>
        </div>

        <div class="content">
            <transition 
                :enter-active-class="enterAnimation" 
                :leave-active-class="leaveAnimation" 
                mode="out-in"
            >
                <keep-alive v-if="keepAliveData">
                    <component 
                        :is="steps[currentStep.index].component" 
                        :clickedNext="nextButton[currentStep.name]" 
                        :current-step="currentStep"
                        :model="model"

                        @can-continue="proceed" 
                        @change-next="changeNextBtnValue" 
                        @emit-model="emitModel"
                    />
                </keep-alive>

                <!--Show component and destroy it in each step change-->
                <component 
                    v-else 
                    :is="steps[currentStep.index].component" 
                    :clickedNext="nextButton[currentStep.name]" 
                    :current-step="currentStep"
                    :model="model"

                    @can-continue="proceed"
                    @change-next="changeNextBtnValue" 
                    @emit-model="emitModel"
                />
            </transition>
        </div>

        <div :class="['bottom', (currentStep.index > 0) ? '' : 'only-next']">
            <div 
                v-if="currentStep.index > 0" 
                class="stepper-button previous" 
                @click="backStep()"
            >
                <i class="material-icons">keyboard_arrow_left</i>
                <span>{{ 'Back' }}</span>
            </div>

            <div 
                :class="['stepper-button next', !canContinue ? 'deactivated' : '']" 
                @click="nextStep()"
            >
                <span>{{ (finalStep) ? 'Finish' : 'Next' }}</span>
                <i class="material-icons">keyboard_arrow_right</i>
            </div>
        </div>
    </div>
</template>

<script>

/* 

    Component Procured from: 
    https://github.com/PygmySlowLoris/vue-stepper/blob/master/src/HorizontalStepper.vue

    Issue was that npm was listed as a dependendency: 
        "npm": "^5.10.0",

    Thus the "cloning" of the component.
    A good side effect is that we can integrate Bootstrap components directly.

*/


export default {

    props: {
        topButtons: { type: Boolean, default: false },
        keepAlive: { type: Boolean, default: true },
        reset: { type: Boolean, default: false },
        steps: { type: Array },
        model: { type: Object },
        setStep: { type: Number }
    },

    data() {
        return {
            currentStep: {},
            previousStep: {},
            nextButton: {},
            canContinue: false,
            finalStep: false,
            keepAliveData: this.keepAlive
        }
    },

    computed: {
        enterAnimation() {
            return this.currentStep.index < this.previousStep.index
                ?   "animated quick fadeInLeft"
                :   "animated quick fadeInRight"
        },

        leaveAnimation() {
            return this.currentStep.index > this.previousStep.index 
                ?   "animated quick fadeOutLeft"
                :   "animated quick fadeOutRight"
        }
    },


    methods: {
        isStepActive(index, step) {
            return  this.currentStep.index === index
                ?   "activated"
                :   "deactivated"
        },

        activateStep(index, back = false) {
            if (this.steps[index]) {
                this.previousStep = this.currentStep

                this.currentStep = {
                    name: this.steps[index].name,
                    index: index
                }

                this.finalStep = index + 1 === this.steps.length ? true : false
                if (!back) { this.$emit("completed-step", this.previousStep) }
            }

            this.$emit("active-step", this.currentStep)
        },

        nextStepAction() {
            this.nextButton[this.currentStep.name] = true
            if (this.canContinue) {
                if (this.finalStep){ this.$emit("stepper-finished", this.currentStep) }
                const currentIndex = this.currentStep.index + 1
                this.activateStep(currentIndex)
            }
//            TO DO: Uncomment when enabling form validation.
//            this.canContinue = false
            this.$forceUpdate()
        },

        nextStep () {
            if (!this.$listeners || !this.$listeners['before-next-step']){ this.nextStepAction() }

//            TO DO: Uncomment when enabling form validation.
//            this.canContinue = false
            this.$emit(
                "before-next-step", 
                { currentStep: this.currentStep }, 
                (next = true) => {
                    this.canContinue = true
                    if (next) {this.nextStepAction()}
                }
            )
        },

        backStep() {
            this.$emit("clicking-back")
            this.canContinue = true
            const currentIndex = this.currentStep.index - 1
            if (currentIndex >= 0){ this.activateStep(currentIndex, true) }
        },

        proceed(payload){ this.canContinue = payload.value },
        changeNextBtnValue(payload) {
            this.nextButton[this.currentStep.name] = payload.nextBtnValue
            this.$forceUpdate()
        },

        emitModel(subModel){
            this.$emit('emit-model', subModel)            
        },

        init() {
            this.activateStep(0)
            this.steps.forEach(step => { this.nextButton[step.name] = false })
        }
    },

    watch: {
        reset(val) {
            if(!val) {return}
            this.keepAliveData = false
            this.init()
            this.previousStep = {}
            this.$nextTick(() => {
                this.keepAliveData = this.keepAlive
                this.$emit('reset', true)
            })
        },

        setStep(val){
            if(!val) {return}
            this.activateStep(val)
        }
    },

    created() {this.init()}
}

</script>

<style src="../style/Stepper.scss" scoped lang="scss"></style>
<style scoped>

    @font-face {
        font-family: "Material Icons";
        font-style: normal;
        font-weight: 400;
        src: local("Material Icons"), local("MaterialIcons-Regular"),
            url(https://fonts.gstatic.com/s/materialicons/v17/2fcrYFNaTjcS6g4U3t-Y5ZjZjT5FdEJ140U2DJYC3mY.woff2)
            format("woff2");
    }

    .material-icons {
        font-family: "Material Icons";
        font-weight: normal;
        font-style: normal;
        font-size: 24px;
        line-height: 1;
        letter-spacing: normal;
        text-transform: none;
        display: inline-block;
        white-space: nowrap;
        word-wrap: normal;
        direction: ltr;
        font-feature-settings: "liga";
        -webkit-font-feature-settings: "liga";
        -webkit-font-smoothing: antialiased;
    }

</style>
